import React, { useState, useEffect, useRef } from "react";
import {
  FiChevronDown,
  FiX,
  FiCheck,
  FiBookOpen,
  FiFolder,
  FiLoader,
  FiLayers
} from "react-icons/fi";
import api from "../connector/api";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

// Tab
import ClassroomDetailTab from "./components/tab/ClassroomDetailTab";

// Helper function to convert grade levels into a numeric rank for ordering
const parseGradeRank = (gradeStr = "", classLabel = "") => {
  const combined = `${gradeStr} ${classLabel}`.toLowerCase();

  if (combined.includes("pre") || combined.includes("k1") || combined.includes("nursery")) return 0;
  if (combined.includes("kind") || combined.includes("kg") || combined.includes("k2")) return 0.5;

  // Match numbers (e.g., "Grade 1", "Class 12", "11")
  const match = combined.match(/\b\d+\b/);
  if (match) {
    return parseInt(match[0], 10);
  }

  return 99; // Fallback to end of list if unmapped
};

export default function ClassroomManager({ assignedClassesList = [] }) {
  // Dynamic Classroom Options State (Fetched from API)
  const [classroomOptions, setClassroomOptions] = useState([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Dynamic Tab States
  const [activeTabs, setActiveTabs] = useState([]);
  const [currentTabId, setCurrentTabId] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // User & Layout States
  const [permissions, setPermissions] = useState([]);
  const [user, setUser] = useState({ first_name: "Teacher", last_name: "User", email: "teacher@school.edu" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingPeriod, setLoadingPeriod] = useState(false);

  const notificationDropdownRef = useRef(null);

  // Load Session Data
  useEffect(() => {
    const storedPerms = sessionStorage.getItem("permissions");
    const storedUser = sessionStorage.getItem("user");

    if (storedPerms) {
      try {
        setPermissions(JSON.parse(storedPerms));
      } catch (err) {
        console.error("Failed to parse permissions:", err);
      }
    }

    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        setUser({
          first_name: userObj.first_name || "Teacher",
          last_name: userObj.last_name || "User",
          username: userObj.username || "teacher",
          email: userObj.email || "teacher@school.edu"
        });
      } catch (err) {
        console.error("Failed to parse user session:", err);
      }
    }
  }, []);

  // Fetch all Paginated Classrooms from DRF endpoint
  useEffect(() => {
    const fetchAllClassrooms = async () => {
      setLoadingClassrooms(true);
      setFetchError(null);

      try {
        let allResults = [];
        // Pass path relative to baseURL (no leading /api/)
        let url = "academic/classroom/";

        while (url) {
          const response = await api.get(url);
          const data = response.data;

          if (Array.isArray(data)) {
            allResults = [...allResults, ...data];
            url = null;
          } else if (data && data.results) {
            allResults = [...allResults, ...data.results];
            url = data.next
              ? data.next.replace(/^https?:\/\/[^\/]+(\/api\/)?/, "")
              : null;
          } else {
            url = null;
          }
        }

        const formattedClassrooms = allResults.map((item) => {
          const label = item.name || item.class_name || `Class ${item.id}`;
          const category = item.grade_level ? `Grade ${item.grade_level}` : item.section || "Academic Class";
          const gradeRank = parseGradeRank(item.grade_level || "", label);

          return {
            id: `classroom_${item.id}`,
            rawId: item.id,
            label,
            category,
            gradeRank,
            code: item.code || item.room_number || "",
            icon: FiBookOpen,
            description: item.description || `Manage details and workspace for ${label}.`
          };
        });

        formattedClassrooms.sort((a, b) => {
          if (a.gradeRank !== b.gradeRank) {
            return a.gradeRank - b.gradeRank;
          }
          return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: "base" });
        });

        setClassroomOptions(formattedClassrooms);
      } catch (err) {
        console.error("Error fetching classrooms:", err);
        setFetchError("Failed to load classrooms");
      } finally {
        setLoadingClassrooms(false);
      }
    };

    fetchAllClassrooms();
  }, []);

  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    window.location.href = "/login";
  };

  const yearDisplay = "2025/2026";
  const termDisplay = "Term 1";

  // Dropdown Selection Handlers
  const handleSelectOption = (option) => {
    setIsDropdownOpen(false);

    if (!activeTabs.some((tab) => tab.id === option.id)) {
      setActiveTabs((prev) => [...prev, option]);
    }
    setCurrentTabId(option.id);
  };

  const handleCloseTab = (e, optionId) => {
    e.stopPropagation();
    const updatedTabs = activeTabs.filter((tab) => tab.id !== optionId);
    setActiveTabs(updatedTabs);

    if (currentTabId === optionId) {
      if (updatedTabs.length > 0) {
        setCurrentTabId(updatedTabs[updatedTabs.length - 1].id);
      } else {
        setCurrentTabId(null);
      }
    }
  };

  const activeOption = activeTabs.find((tab) => tab.id === currentTabId);

  // Dynamic Workspace Content Handler
  const renderTabContent = () => {
    if (!activeOption) return null;

    return (
      <ClassroomDetailTab
        key={activeOption.id}
        classId={activeOption.rawId}
        classData={activeOption}
      />
    );
  };

  return (
    <div className="flex h-screen w-full bg-zinc-50/50 overflow-hidden font-dynamic-title">
      {/* Fixed Sidebar Component */}
      <Sidebar
        className="no-print"
        permissions={permissions}
        user={user}
        teacherSubjects={assignedClassesList}
        handleLogout={handleLogout}
        onOpenChangePassword={() => setIsModalOpen(true)}
      />

      {/* Main Layout Area */}
      <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {/* Header Component */}
        <Header
          loading={loadingPeriod}
          loadingPeriod={loadingPeriod}
          yearDisplay={yearDisplay}
          termDisplay={termDisplay}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          notifOpen={notifOpen}
          setNotifOpen={setNotifOpen}
          notificationDropdownRef={notificationDropdownRef}
        />

        {/* Workspace Body */}
        <div className="flex-1 overflow-y-auto flex flex-col min-w-0 custom-scroll">
          {/* Sub Header & Custom Dropdown */}
          <div className="p-4 bg-white border-b border-zinc-200/80 flex flex-col sm:flex-row sm:items-center justify-between gap-4 shrink-0">
            <div>
              <h1 className="text-sm font-bold text-zinc-900 tracking-tight">Classroom Manager</h1>
              <p className="text-[11px] text-zinc-400">
                Select a class from the list (Pre School to Grade 12) to open inside dynamic tabs.
              </p>
            </div>

            {/* Custom Dropdown */}
            <div className="relative w-full sm:w-64">
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between px-3 py-2 bg-zinc-50 border border-zinc-200/80 rounded-lg text-zinc-700 hover:bg-zinc-100/80 focus:outline-none transition-colors text-xs font-medium"
              >
                <div className="flex items-center gap-2 truncate">
                  <FiLayers className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="truncate">
                    {loadingClassrooms ? "Loading Classes..." : "Select Classroom..."}
                  </span>
                </div>
                {loadingClassrooms ? (
                  <FiLoader className="w-4 h-4 text-zinc-400 animate-spin shrink-0" />
                ) : (
                  <FiChevronDown
                    className={`w-4 h-4 text-zinc-400 transition-transform duration-200 ${
                      isDropdownOpen ? "rotate-180" : ""
                    }`}
                  />
                )}
              </button>

              {/* Custom Dropdown Menu */}
              {isDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsDropdownOpen(false)}
                  />
                  <div className="absolute right-0 mt-1 w-full bg-white border border-zinc-200 rounded-lg shadow-xl py-1 z-30 max-h-60 overflow-y-auto custom-scroll">
                    <div className="px-3 py-1.5 border-b border-zinc-100 text-[10px] font-bold text-zinc-400 uppercase tracking-wider">
                      Available Classrooms ({classroomOptions.length})
                    </div>

                    {fetchError && (
                      <div className="px-3 py-2 text-xs text-red-500">{fetchError}</div>
                    )}

                    {!loadingClassrooms && classroomOptions.length === 0 && !fetchError && (
                      <div className="px-3 py-2 text-xs text-zinc-400">No classrooms found</div>
                    )}

                    {classroomOptions.map((option) => {
                      const isOpen = activeTabs.some((tab) => tab.id === option.id);
                      const IconComponent = option.icon;

                      return (
                        <button
                          key={option.id}
                          onClick={() => handleSelectOption(option)}
                          className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-zinc-50 transition-colors"
                        >
                          <div className="flex items-center gap-2.5 min-w-0 pr-2">
                            <IconComponent className="w-3.5 h-3.5 text-zinc-400 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-zinc-800 truncate">
                                {option.label}
                              </p>
                              <p className="text-[10px] text-zinc-400">{option.category}</p>
                            </div>
                          </div>
                          {isOpen && <FiCheck className="w-3.5 h-3.5 text-zinc-600 shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Dynamic Tabs Bar */}
          {activeTabs.length > 0 && (
            <div className="flex items-center gap-1 px-4 pt-2 bg-white border-b border-zinc-200/80 overflow-x-auto scrollbar-none shrink-0">
              {activeTabs.map((tab) => {
                const isActive = tab.id === currentTabId;
                const IconComponent = tab.icon;

                return (
                  <div
                    key={tab.id}
                    onClick={() => setCurrentTabId(tab.id)}
                    className={`group flex items-center gap-2 px-3 py-2 rounded-t-lg border-t border-x cursor-pointer transition-all text-xs font-semibold shrink-0 select-none ${
                      isActive
                        ? "bg-zinc-50 border-zinc-200/80 text-zinc-900 border-b-transparent -mb-px"
                        : "bg-zinc-100/60 border-transparent text-zinc-500 hover:bg-zinc-100 hover:text-zinc-800"
                    }`}
                  >
                    <IconComponent
                      className={`w-3.5 h-3.5 ${isActive ? "text-zinc-700" : "text-zinc-400"}`}
                    />
                    <span>{tab.label}</span>
                    <button
                      onClick={(e) => handleCloseTab(e, tab.id)}
                      className="p-0.5 rounded-md hover:bg-zinc-200/80 text-zinc-400 hover:text-zinc-700 transition-colors"
                    >
                      <FiX className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Active Workspace View */}
          <div className="flex-1 pl-6 pr-6 pb-6 pt-2">
            {activeOption ? (
              <div className="rounded-lg p-2 shadow-xs min-h-[400px]">
                {renderTabContent()}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 border border-dashed border-zinc-200 rounded-xl bg-white/50 text-center">
                <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-400 mb-3">
                  <FiFolder className="w-5 h-5" />
                </div>
                <p className="text-xs font-bold text-zinc-800">No Classroom Selected</p>
                <p className="text-[11px] text-zinc-400 max-w-xs mt-0.5">
                  Select a class from the custom dropdown menu above to view and manage its workspace.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}